/**
 * Listener sets keyed by the thing they follow, with the unsubscribe
 * returned beside the subscribe. A key's set is dropped with its last
 * listener, so a key nothing follows any more holds nothing here.
 */
export class KeyedListeners<K> {
    private readonly listeners = new Map<K, Set<() => void>>();

    public subscribe(key: K, listener: () => void): () => void {
        const set = this.listeners.get(key) ?? new Set<() => void>();
        this.listeners.set(key, set);
        set.add(listener);
        return () => {
            set.delete(listener);
            // Only the set still registered is dropped: a stale unsubscribe
            // must not remove a later subscriber's set.
            if (set.size === 0 && this.listeners.get(key) === set) {
                this.listeners.delete(key);
            }
        };
    }

    /** Runs every listener of `key`, in subscription order. */
    public notify(key: K): void {
        const set = this.listeners.get(key);
        if (!set) {
            return;
        }
        // A listener may subscribe or unsubscribe while it runs; iterate a
        // snapshot so the walk neither skips nor re-enters one.
        for (const listener of [...set]) {
            listener();
        }
    }

    /** How many listeners follow `key`. */
    public countFor(key: K): number {
        return this.listeners.get(key)?.size ?? 0;
    }
}
